import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import {
  Envelope,
  PaperPlaneTilt,
  Lightning,
  Confetti,
  Sparkle,
  Storefront,
  CalendarBlank,
  Crown,
  Sun,
  ChartLineUp,
  Star,
  ClipboardText,
  Trash,
  PlayCircle,
  Eye,
  Rocket,
  CaretDown,
  UserPlus,
  CheckCircle,
  ArrowRight,
  X,
} from "@phosphor-icons/react";

/**
 * EmailDispatchCenter — single source of truth for every email send.
 *
 * Replaces the previous three separate cards (Cron triggers, Launch
 * campaigns, Email previews) with one dropdown picker + contextual
 * action buttons per entry.
 *
 * Entry types:
 *  - "cron"     — fires a scheduled email job NOW (e.g. Sunday digest).
 *                  Action: "Run now". Optional: "Send preview to me".
 *  - "campaign" — one-shot mass send with idempotency flag (e.g. GCal
 *                  announcement). Actions: "Dry run (preview count)",
 *                  "Send for real", "Send preview to me".
 *  - "preview"  — send a single rendered email to the admin's own
 *                  inbox so the copy can be eyeballed without faking
 *                  state. Action: "Send preview to me".
 *
 * Each entry declares which actions it supports. The action row only
 * renders buttons for actions the entry actually supports.
 */

const ENTRIES = [
  // ============================================================
  // Section: Campaigns (mass one-shots with idempotency)
  // ============================================================
  {
    id: "gcal-announcement",
    section: "Campaigns",
    label: "Google Calendar 2-way sync — Announcement",
    blurb:
      "One-shot blast to all owners. Pro recipients get the how-to walkthrough. Non-Pro recipients get the FOMO version: 'Pros just stopped double-entering — here's what you're missing.' Idempotent.",
    Icon: CalendarBlank,
    actions: ["preview", "dry_run", "send_real"],
    preview_kinds: [
      { kind: "gcal_announcement_pro", label: "Preview · Pro version" },
      { kind: "gcal_announcement_free", label: "Preview · Free version" },
    ],
    campaign_endpoint: "/admin/campaigns/gcal-announcement",
  },
  {
    id: "calendar-sync-announcement",
    section: "Campaigns",
    label: "Calendar Sync trifecta — Pro Announcement",
    blurb:
      "Pro-only blast promoting the COMBINED calendar story: Google 2-way + Outlook 2-way + Apple/Outlook subscription feed. Single send, single idempotency flag (calendar_sync_announcement_email_at). Use this AFTER the standalone GCal announcement to drive adoption of the full bundle.",
    Icon: CalendarBlank,
    actions: ["preview", "dry_run", "send_real"],
    preview_kinds: [
      { kind: "calendar_sync_announcement_pro", label: "Preview · Pro" },
    ],
    campaign_endpoint: "/admin/campaigns/calendar-sync-announcement",
  },
  {
    id: "pro-launch",
    section: "Campaigns",
    label: "Pro Launch — Insider state of Pro",
    blurb:
      "One-shot 'state of Pro' email to every Pro owner. Insider tone — Boost, peak-hour insights, Sunday digest, profile editor, smart push.",
    Icon: Sparkle,
    actions: ["dry_run", "send_real"],
    campaign_endpoint: "/admin/campaigns/pro-launch",
  },
  {
    id: "non-pro-launch",
    section: "Campaigns",
    label: "Non-Pro Launch — Free tier reinforcement",
    blurb:
      "One-shot 'TruckTrackr is and stays free' email to every non-Pro owner. Reinforces free participation, soft Pro intro, invite-a-friend nudge.",
    Icon: Confetti,
    actions: ["dry_run", "send_real"],
    campaign_endpoint: "/admin/campaigns/non-pro-launch",
  },

  // ============================================================
  // Section: Scheduled cron jobs — fire manually on demand
  // ============================================================
  {
    id: "admin-daily-digest",
    section: "Scheduled (run-now)",
    label: "Admin Daily Digest",
    blurb: "Daily 6 am ET — your operational morning email listing every truck active today + pin-accuracy flags.",
    Icon: Sun,
    actions: ["run_now"],
    cron_endpoint: "/admin/cron/admin-daily-digest",
    cadence: "Daily · 6 am ET",
  },
  {
    id: "admin-daily-digest-tomorrow",
    section: "Scheduled (run-now)",
    label: "Daily Digest · Tomorrow",
    blurb: "Preview tomorrow's active-trucks list a day ahead. Good for Saturday-night planning.",
    Icon: Sun,
    actions: ["run_now"],
    cron_endpoint: "/admin/cron/admin-daily-digest?date=tomorrow",
    cadence: "On demand",
  },
  {
    id: "pro-insights-announcement",
    section: "One-shot campaigns",
    label: "Pro Insights · Live now announcement",
    blurb:
      "One-shot — tells every Pro owner that their dashboard Pro Insights cards (peak hours, day intent, top cities) are now live with real data after the range-shadowing bug fix. Idempotent via pro_insights_announce_sent_at flag — safe to re-run.",
    Icon: Sparkle,
    actions: ["preview", "run_now"],
    preview_kinds: [{ kind: "pro_insights_announcement", label: "Preview · Pro Insights live" }],
    cron_endpoint: "/admin/cron/pro-insights-announcement",
    cadence: "Manual · one-shot",
  },
  {
    id: "activation-series",
    section: "Scheduled (run-now)",
    label: "Owner Activation Series (D3 → D30)",
    blurb:
      "Daily 10 am UTC — D3 friendly nudge, D5 FOMO closer, D14 stats-rich follow-up, D30 final exit-survey-style ask. Different copy for Pro vs Free. Idempotent via activation_d{n}_sent_at flags; stops sending the moment the owner posts anything. After D30 the owner falls out permanently.",
    Icon: UserPlus,
    actions: ["preview", "run_now"],
    preview_kinds: [
      { kind: "activation_d3", label: "Preview · D3 · Free" },
      { kind: "activation_d3_pro", label: "Preview · D3 · Pro" },
      { kind: "activation_d5", label: "Preview · D5 · Free" },
      { kind: "activation_d5_pro", label: "Preview · D5 · Pro" },
      { kind: "activation_d14", label: "Preview · D14 · Free" },
      { kind: "activation_d14_pro", label: "Preview · D14 · Pro" },
      { kind: "activation_d30", label: "Preview · D30 · Free" },
      { kind: "activation_d30_pro", label: "Preview · D30 · Pro" },
    ],
    cron_endpoint: "/admin/cron/activation-series",
    cadence: "Daily · 10 am UTC",
    counter_endpoint: "/admin/kpis/activation-series",
    funnel_endpoint: "/admin/kpis/activation-funnel",
  },
  {
    id: "ghost-nudge",
    section: "Scheduled (run-now)",
    label: "Ghost-Owner FOMO",
    blurb: "Tuesday 9 am ET — emails owners who haven't posted anything for the current calendar week.",
    Icon: Lightning,
    actions: ["preview", "run_now"],
    preview_kinds: [{ kind: "ghost_nudge", label: "Preview email" }],
    cron_endpoint: "/admin/cron/ghost-nudge",
    cadence: "Tue · 9 am ET",
  },
  {
    id: "complete-profile-reminder",
    section: "Scheduled (run-now)",
    label: "Complete-Profile Reminder",
    blurb: "Emails owners who have NO truck attached to their account. Gives them a 7-day deadline; 30-day dedup so re-clicking is safe.",
    Icon: ClipboardText,
    actions: ["run_now"],
    cron_endpoint: "/admin/cron/complete-profile-reminder",
    cadence: "On demand",
  },
  {
    id: "flag-inactive-owners",
    section: "Scheduled (run-now)",
    label: "Flag Inactive Owners",
    blurb: "Daily 7:30 am ET — flags owners whose 7-day truck-setup deadline has passed and still have no truck. Idempotent.",
    Icon: Trash,
    actions: ["run_now"],
    cron_endpoint: "/admin/cron/flag-inactive-owners",
    cadence: "Daily · 7:30 am ET",
  },
  {
    id: "weekly-reminders",
    section: "Scheduled (run-now)",
    label: "Weekly Reminder",
    blurb: "Sunday 5 pm ET — polite \"are you on the map this week?\" nudge to owners with no upcoming events.",
    Icon: PaperPlaneTilt,
    actions: ["preview", "run_now"],
    preview_kinds: [{ kind: "weekly_reminder", label: "Preview email" }],
    cron_endpoint: "/admin/cron/weekly-reminders",
    cadence: "Sun · 5 pm ET",
  },
  {
    id: "weekly-digests",
    section: "Scheduled (run-now)",
    label: "Weekly Analytics Digest",
    blurb: "Sunday 5:30 pm ET — engagement-summary digest to owners with ≥1 analytics event in the last 7 days.",
    Icon: ChartLineUp,
    actions: ["run_now"],
    cron_endpoint: "/admin/cron/weekly-digests",
    cadence: "Sun · 5:30 pm ET",
  },
  {
    id: "follower-digests",
    section: "Scheduled (run-now)",
    label: "Follower Weekly Digest",
    blurb: "Sunday 2 pm ET — \"Your trucks are out this week\" email to customers who followed a truck + opted in.",
    Icon: Star,
    actions: ["run_now"],
    cron_endpoint: "/admin/cron/follower-digests",
    cadence: "Sun · 2 pm ET",
  },

  // ============================================================
  // Section: Templates — preview-only, never auto-sent (welcome,
  // follow-feature, etc.). Useful for QA copy without hitting users.
  // ============================================================
  {
    id: "welcome",
    section: "Templates (preview-only)",
    label: "Owner Welcome",
    blurb: "Sent automatically to new owners after signup. Preview the rendered version in your inbox.",
    Icon: Confetti,
    actions: ["preview"],
    preview_kinds: [{ kind: "welcome", label: "Preview email" }],
  },
  {
    id: "follow-announcement",
    section: "Templates (preview-only)",
    label: "Follow-Feature Announcement",
    blurb: "One-time announcement to existing owners about the Follow Truck feature. Sent through the in-page Truck-help-box launcher.",
    Icon: Sparkle,
    actions: ["preview"],
    preview_kinds: [{ kind: "follow_announcement", label: "Preview email" }],
  },
  {
    id: "pro-features-announcement",
    section: "Templates (preview-only)",
    label: "Pro Features Announcement",
    blurb: "One-time-per-Pro recap email of every Pro perk plus the public profile editor.",
    Icon: Storefront,
    actions: ["preview"],
    preview_kinds: [{ kind: "pro_features_announcement", label: "Preview email" }],
  },
];

const SECTIONS = ["Campaigns", "Scheduled (run-now)", "Templates (preview-only)"];

export default function EmailDispatchCenter() {
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState(ENTRIES[0].id);
  const [busy, setBusy] = useState(null);
  const [stats, setStats] = useState(null);
  const [counters, setCounters] = useState(null);
  const [funnel, setFunnel] = useState(null);

  const entry = ENTRIES.find((e) => e.id === selectedId) || ENTRIES[0];

  // Lifecycle-series counters (D3/D5 etc) — fetched on demand when the
  // admin selects an entry that exposes a `counter_endpoint`. Triggered
  // by `loadCounters()` from the picker onChange + after `runCron()`.
  // No on-mount fetch — the default-selected entry has no counters.
  const loadCounters = async (entry) => {
    if (!entry?.counter_endpoint) {
      setCounters(null);
      setFunnel(null);
      return;
    }
    try {
      const r = await api.get(entry.counter_endpoint);
      setCounters(r.data || null);
    } catch {
      setCounters(null);
    }
    if (entry.funnel_endpoint) {
      try {
        const f = await api.get(entry.funnel_endpoint);
        setFunnel(f.data || null);
      } catch {
        setFunnel(null);
      }
    } else {
      setFunnel(null);
    }
  };

  if (!user) return null;

  const sendPreview = async (kind) => {
    setBusy(`preview:${kind}`);
    try {
      const r = await api.post("/admin/email-preview", { kind });
      toast.success(`Preview sent to ${r.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e, "Preview send failed"));
    } finally {
      setBusy(null);
    }
  };

  const runCampaign = async (dryRun) => {
    if (!entry.campaign_endpoint) return;
    if (!dryRun && !window.confirm(`Send "${entry.label}" to all eligible recipients? This bypasses dry-run.`)) return;
    setBusy(dryRun ? "dry_run" : "send_real");
    setStats(null);
    try {
      const r = await api.post(`${entry.campaign_endpoint}?dry_run=${dryRun ? "true" : "false"}`);
      const payload = r.data ?? {};
      if (payload.queued) {
        setStats({
          dry_run: false,
          would_send: payload.expected_recipients,
          sent: 0,
          background: true,
          message: payload.message,
        });
        toast.success(payload.message || `Sending in background for ~${payload.expected_recipients} owner(s).`);
      } else {
        const s = payload.stats ?? null;
        setStats(s);
        if (dryRun) {
          toast.success(`Dry run complete: ${s?.would_send ?? 0} would receive.`);
        } else {
          toast.success(`Sent ${s?.sent ?? 0} · failed ${s?.failed ?? 0} · skipped ${s?.skipped_already ?? 0}.`);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e, `${entry.label} failed`));
    } finally {
      setBusy(null);
    }
  };

  const runCron = async () => {
    if (!entry.cron_endpoint) return;
    if (!window.confirm(`Run "${entry.label}" now? This sends real emails to qualifying owners (respecting dedup).`)) return;
    setBusy("run_now");
    try {
      const { data } = await api.post(entry.cron_endpoint);
      toast.success(`${entry.label} ran. ${formatCronResult(data) || (data?.message ?? "")}`);
      // Refresh counters if this entry exposes them — gives the admin
      // instant feedback after firing the batch. Slight delay so the
      // background task has a moment to write the first rows.
      if (entry.counter_endpoint) {
        setTimeout(() => loadCounters(entry), 2500);
      }
    } catch (e) {
      toast.error(formatApiError(e, `${entry.label} failed`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="email-dispatch-center">
      <div className="flex items-center gap-2 mb-1">
        <Envelope size={18} weight="duotone" className="text-primary" />
        <h2 className="font-display font-semibold text-lg tracking-tight" data-testid="email-dispatch-heading">
          Email dispatch
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        One stop for every email TruckTrackr sends — campaigns, scheduled crons, and template previews.
        Pick a recipient flow below and choose what to send.
      </p>

      <label className="block">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          Choose an email
        </span>
        <div className="relative mt-1">
          <select
            value={selectedId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedId(id);
              setStats(null);
              const next = ENTRIES.find((x) => x.id === id);
              loadCounters(next || null);
            }}
            className="w-full appearance-none rounded-xl border border-border bg-background px-3.5 py-2.5 pr-9 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="email-dispatch-picker"
          >
            {SECTIONS.map((sec) => (
              <optgroup key={sec} label={sec}>
                {ENTRIES.filter((e) => e.section === sec).map((e) => (
                  <option key={e.id} value={e.id}>{e.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <CaretDown weight="bold" className="h-4 w-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </label>

      {/* Entry card */}
      <div className="mt-4 rounded-xl border border-border bg-background/50 p-4">
        <div className="flex items-start gap-3">
          <entry.Icon size={22} weight="duotone" className="text-primary mt-0.5 flex-none" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
              {entry.label}
              {entry.cadence && (
                <span className="text-[10px] uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 font-bold text-muted-foreground">
                  {entry.cadence}
                </span>
              )}
            </div>
            <p className="text-[13px] text-muted-foreground leading-relaxed mt-1.5">{entry.blurb}</p>
          </div>
        </div>

        {/* Lifecycle counters (D3/D5 etc) — only when an endpoint is configured */}
        {entry.counter_endpoint && counters?.campaigns && (
          <div
            className="mt-4 grid grid-cols-2 gap-2"
            data-testid="email-dispatch-counters"
          >
            {counters.campaigns.map((c) => (
              <div
                key={c.step}
                className="rounded-xl border border-border bg-card px-3 py-2.5"
                data-testid={`counter-${c.step.toLowerCase()}`}
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                  {c.step}
                </div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="font-display font-bold text-xl tabular-nums">{c.sent}</span>
                  <span className="text-[11px] text-muted-foreground">sent</span>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  <span className="font-semibold text-foreground tabular-nums">{c.clicks}</span> clicks ·{" "}
                  <span className="font-semibold text-foreground tabular-nums">{c.ctr_pct}%</span> CTR
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activation series funnel — only when funnel_endpoint configured */}
        {entry.funnel_endpoint && funnel && (
          <ActivationFunnelPanel funnel={funnel} />
        )}

        {/* Action buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {entry.actions.includes("preview") && entry.preview_kinds?.map((pk) => (
            <Button
              key={pk.kind}
              size="sm"
              variant="outline"
              onClick={() => sendPreview(pk.kind)}
              disabled={busy !== null}
              data-testid={`email-dispatch-preview-${pk.kind}`}
            >
              <Eye size={14} weight="bold" className="mr-1" />
              {busy === `preview:${pk.kind}` ? "Sending…" : pk.label}
            </Button>
          ))}
          {entry.actions.includes("dry_run") && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => runCampaign(true)}
              disabled={busy !== null}
              data-testid="email-dispatch-dry-run"
            >
              <Eye size={14} weight="bold" className="mr-1" />
              {busy === "dry_run" ? "Counting…" : "Dry run"}
            </Button>
          )}
          {entry.actions.includes("send_real") && (
            <Button
              size="sm"
              onClick={() => runCampaign(false)}
              disabled={busy !== null}
              data-testid="email-dispatch-send-real"
            >
              <Rocket size={14} weight="bold" className="mr-1" />
              {busy === "send_real" ? "Sending…" : "Send for real"}
            </Button>
          )}
          {entry.actions.includes("run_now") && (
            <Button
              size="sm"
              onClick={runCron}
              disabled={busy !== null}
              data-testid="email-dispatch-run-now"
            >
              <PlayCircle size={14} weight="bold" className="mr-1" />
              {busy === "run_now" ? "Running…" : "Run now"}
            </Button>
          )}
        </div>

        {/* Stats / background-task indicator */}
        {stats?.background && (
          <div
            className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
            data-testid="email-dispatch-background"
          >
            ⚡ Sending in background for ~{stats.would_send} owner{stats.would_send === 1 ? "" : "s"}.
            Click <em>Dry run</em> again in a few minutes — when &ldquo;would send&rdquo; hits 0 the campaign is fully delivered.
          </div>
        )}
        {stats && !stats.background && (
          <div
            className="mt-3 rounded-lg border border-border bg-muted/30 px-3 py-2 text-[12px] text-foreground"
            data-testid="email-dispatch-stats"
          >
            {stats.dry_run ? (
              <>
                <strong>{stats.would_send ?? 0}</strong> would receive ·{" "}
                already-sent {stats.skipped_already ?? 0} ·{" "}
                test {stats.skipped_test ?? 0} ·{" "}
                no-email {stats.skipped_no_email ?? 0}
              </>
            ) : (
              <>
                Sent <strong>{stats.sent ?? 0}</strong> ·{" "}
                failed {stats.failed ?? 0} ·{" "}
                skipped {stats.skipped_already ?? 0}
              </>
            )}
          </div>
        )}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">
        Previews land in <span className="font-medium text-foreground">{user.email}</span>.
        Campaigns are idempotent — each owner is emailed at most once via a per-user flag.
      </p>
    </section>
  );
}


/** Compact relative-time formatter for the recent-graduations feed.
 *  e.g. "just now", "3m ago", "2h ago", "5d ago". */
function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

/** Format time-to-first-post hours: "4h" / "2.3d" / "<1h" */
function formatTtf(hours) {
  if (hours == null) return "—";
  if (hours < 1) return "<1h";
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}


function formatCronResult(data) {
  if (!data || typeof data !== "object") return "";
  const parts = [];
  for (const key of ["sent", "would_send", "queued", "flagged", "updated", "skipped", "failed"]) {
    if (typeof data[key] === "number") parts.push(`${key}: ${data[key]}`);
  }
  return parts.join(" · ");
}


/**
 * ActivationFunnelPanel — renders the D3 → D30 conversion funnel
 * plus completion outcomes. Self-contained, takes a single `funnel`
 * prop matching the shape returned by `/api/admin/kpis/activation-funnel`.
 *
 * Visual hierarchy:
 *   1. Headline conversion KPI (the only number that really matters)
 *   2. Per-step horizontal flow (sent + retention% on the arrows)
 *   3. Outcomes breakdown (where owners ended up)
 */
function ActivationFunnelPanel({ funnel }) {
  const [cohort, setCohort] = useState(null); // { id, label } | null
  const [grads, setGrads] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  const runBackfill = async () => {
    if (!window.confirm(
      "Backfill first_post_at for legacy graduates?\n\n" +
      "Walks every owner missing the field and stamps it from their earliest db.trucks or db.schedules record. " +
      "Fixes the 'Posted at' time in this modal for owners who graduated before per-post stamping shipped. " +
      "Safe to re-run — idempotent."
    )) return;
    setBackfillBusy(true);
    try {
      const r = await api.post("/admin/backfill/first-post-at");
      const s = r.data || {};
      toast.success(
        `Backfill done · ${s.stamped_from_trucks || 0} from trucks, ${s.stamped_from_schedules || 0} from schedules. Re-open this panel to see the corrected timestamps.`,
        { duration: 12000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  };

  // Recent graduations feed — last 10 owners who posted their first
  // stop. Loaded once on mount; cheap query so we don't poll.
  useEffect(() => {
    let on = true;
    api
      .get("/admin/recent-graduations/list?limit=10")
      .then((r) => on && setGrads(r.data?.items || []))
      .catch(() => on && setGrads([]));
    return () => { on = false; };
  }, []);

  if (!funnel || !funnel.steps || !funnel.outcomes) return null;
  const { steps, kickstart, outcomes } = funnel;
  const headlinePct = outcomes.posted_conversion_pct ?? 0;
  const maxSent = Math.max(1, ...steps.map((s) => s.sent || 0));

  return (
    <div
      className="mt-4 rounded-xl border border-border bg-card overflow-hidden"
      data-testid="activation-funnel-panel"
    >
      {/* Headline KPI */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-center gap-2.5">
          <Confetti weight="duotone" className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Activation conversion
            </div>
            <div className="font-display font-bold text-lg tabular-nums leading-tight" data-testid="funnel-conversion-pct">
              {headlinePct}%
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                of D3 recipients ultimately posted
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCohort({ id: "in_flight", label: "In-flight" })}
          className="text-right hover:bg-muted/40 rounded-lg px-2 py-1 -mr-2 transition"
          data-testid="funnel-in-flight-btn"
        >
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">In-flight</div>
          <div className="font-display font-bold text-lg tabular-nums" data-testid="funnel-in-flight">{outcomes.in_flight}</div>
        </button>
      </div>

      {/* Step flow */}
      <div className="px-4 py-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
          Step-over-step retention
        </div>
        <div className="flex items-stretch gap-1">
          {steps.map((s, i) => (
            <div key={s.step} className="flex items-stretch gap-1 flex-1 min-w-0">
              <button
                type="button"
                onClick={() =>
                  s.sent > 0 &&
                  setCohort({ id: s.step.toLowerCase(), label: `${s.step} sent` })
                }
                disabled={s.sent === 0}
                className="flex-1 min-w-0 rounded-lg border border-border bg-background px-2.5 py-2 relative overflow-hidden text-left enabled:hover:border-primary/40 enabled:hover:bg-primary/5 disabled:cursor-default transition"
                data-testid={`funnel-step-${s.step.toLowerCase()}`}
              >
                {/* Sent-volume bar background — gives an immediate visual sense of cohort shrinkage. */}
                <div
                  className="absolute inset-y-0 left-0 bg-primary/10"
                  style={{ width: `${(s.sent / maxSent) * 100}%` }}
                  aria-hidden
                />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                    {s.step}
                  </div>
                  <div className="font-display font-bold text-base tabular-nums">{s.sent}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">
                    {s.clicks} clicks · {s.ctr_pct}% CTR
                  </div>
                </div>
              </button>
              {i < steps.length - 1 && (
                <div className="flex flex-col items-center justify-center text-[10px] font-semibold text-muted-foreground tabular-nums w-10 shrink-0">
                  <ArrowRight weight="bold" className="h-3 w-3 mb-0.5" />
                  <span data-testid={`funnel-retention-${steps[i + 1].step.toLowerCase()}`}>
                    {steps[i + 1].retention_from_prev_pct ?? 0}%
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Outcomes breakdown */}
      <div className="px-4 pb-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
          Where they ended up
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <OutcomeTile
            label="Posted (graduated)"
            value={outcomes.completed_posted}
            tone="success"
            Icon={CheckCircle}
            testId="outcome-posted"
            sub="The goal"
            onClick={() =>
              outcomes.completed_posted > 0 &&
              setCohort({ id: "posted", label: "Graduated — posted" })
            }
          />
          <OutcomeTile
            label="D30 final"
            value={outcomes.completed_d30_final}
            tone="muted"
            testId="outcome-d30-final"
            sub="Aged out"
            onClick={() =>
              outcomes.completed_d30_final > 0 &&
              setCohort({ id: "d30_final", label: "Aged out at D30" })
            }
          />
          <OutcomeTile
            label="Kickstart"
            value={outcomes.completed_kickstart}
            tone="orange"
            Icon={Rocket}
            testId="outcome-kickstart"
            sub="Backlog batch"
            onClick={() =>
              outcomes.completed_kickstart > 0 &&
              setCohort({ id: "completed_kickstart", label: "Completed via Kickstart" })
            }
          />
          <OutcomeTile
            label="Kickstart sent"
            value={kickstart?.sent ?? 0}
            tone="muted"
            testId="outcome-kickstart-sent"
            sub={`${kickstart?.clicks ?? 0} clicks · ${kickstart?.ctr_pct ?? 0}% CTR`}
            onClick={() =>
              (kickstart?.sent ?? 0) > 0 &&
              setCohort({ id: "kickstart", label: "Kickstart — all sent" })
            }
          />
        </div>
      </div>

      {/* Recent graduations mini-feed — rolling success log of the last
          10 owners who posted their first stop. Each row shows time-to-
          first-post so the admin can spot how fast the funnel is moving. */}
      {grads && grads.length > 0 && (
        <div className="px-4 pb-4 -mt-1" data-testid="funnel-recent-graduations">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              Recent graduations
            </div>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={runBackfill}
                disabled={backfillBusy}
                className="text-[10px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-60 transition"
                data-testid="funnel-backfill-first-post-at"
                title="Fix 'Posted at' timestamps for legacy graduates by stamping first_post_at from their earliest record. Safe + idempotent."
              >
                {backfillBusy ? "Backfilling…" : "Backfill timestamps"}
              </button>
              <button
                type="button"
                onClick={() =>
                  setCohort({ id: "posted", label: "Graduated — posted (full list)" })
                }
                className="text-[10px] font-semibold text-primary hover:underline"
                data-testid="funnel-graduations-see-all"
              >
                See all →
              </button>
            </div>
          </div>
          <ul className="divide-y divide-border rounded-lg border border-border bg-background">
            {grads.map((g) => (
              <li
                key={g.owner_id}
                className="px-3 py-2 text-[12px] flex items-center gap-2"
                data-testid={`funnel-graduation-row-${g.owner_id}`}
              >
                <span className="text-base">🎉</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold truncate text-foreground">
                      {g.truck_name || g.name || "(no truck name)"}
                    </span>
                    {g.is_pro && (
                      <span className="text-[9px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-1 py-0.5 font-bold">
                        Pro
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground text-[11px] truncate">{g.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {relativeTime(g.first_post_at)}
                  </div>
                  {typeof g.time_to_first_post_hours === "number" && (
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {formatTtf(g.time_to_first_post_hours)} to first post
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {cohort && (
        <FunnelCohortModal
          cohortId={cohort.id}
          cohortLabel={cohort.label}
          onClose={() => setCohort(null)}
        />
      )}
    </div>
  );
}

function OutcomeTile({ label, value, sub, tone = "muted", Icon, testId, onClick }) {
  const toneClasses = {
    success: "border-emerald-400/40 bg-emerald-50/60 dark:bg-emerald-500/10",
    orange: "border-orange-400/40 bg-orange-50/60 dark:bg-orange-500/10",
    muted: "border-border bg-background",
  };
  const accent = {
    success: "text-emerald-700 dark:text-emerald-300",
    orange: "text-orange-700 dark:text-orange-300",
    muted: "text-foreground",
  };
  const disabled = !onClick || value === 0;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`rounded-lg border px-2.5 py-2 text-left ${toneClasses[tone]} enabled:hover:brightness-[1.02] enabled:hover:shadow-sm disabled:cursor-default transition`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
        {Icon && <Icon weight="duotone" className={`h-3 w-3 ${accent[tone]}`} />}
        {label}
      </div>
      <div className={`font-display font-bold text-lg tabular-nums ${accent[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
    </button>
  );
}


/**
 * FunnelCohortModal — loads /admin/kpis/activation-funnel/list for a
 * given cohort id and shows the list of owners in that bucket. Used by
 * every clickable tile in the funnel panel — D3/D5/D14/D30 sent, the
 * outcome breakdown tiles, and the in-flight count.
 */
function FunnelCohortModal({ cohortId, cohortLabel, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const r = await api.get(`/admin/kpis/activation-funnel/list?cohort=${encodeURIComponent(cohortId)}`);
        if (on) setData(r.data || { items: [], count: 0 });
      } catch {
        if (on) setData({ items: [], count: 0 });
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [cohortId]);

  const items = data?.items || [];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid={`funnel-cohort-modal-${cohortId}`}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-border bg-card shadow-xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="font-display font-semibold text-lg tracking-tight truncate">
              {cohortLabel}
            </h2>
            {!loading && (
              <span className="text-[10px] uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 font-bold text-muted-foreground tabular-nums">
                {data?.count ?? 0}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-muted/60"
            data-testid="funnel-cohort-modal-close"
            aria-label="Close"
          >
            <X weight="bold" className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No owners in this cohort yet.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((it) => (
                <li
                  key={it.owner_id}
                  className="py-3"
                  data-testid={`funnel-cohort-row-${it.owner_id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-foreground truncate">
                      {it.truck_name || it.name || "(no truck name)"}
                    </span>
                    {it.is_pro && (
                      <span className="text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-1.5 py-0.5 font-bold">
                        Pro
                      </span>
                    )}
                    {it.completed_reason && (
                      <span className="text-[10px] uppercase tracking-wider rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 font-bold">
                        {it.completed_reason}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground truncate">
                    {it.email || "(no email)"}
                    {it.name && it.truck_name && ` · ${it.name}`}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    Signed up: {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                  </div>
                  {/* Label adapts to the cohort so it's never misleading.
                      "posted" → show the REAL first-post timestamp.
                      Step cohorts → "Email sent" (the touchpoint that landed them in this bucket).
                      Others (in-flight) → "D3 sent" since that's the entry point. */}
                  {it.completed_reason === "posted" && it.first_post_at ? (
                    <div className="text-[11px] text-emerald-700 dark:text-emerald-300 font-medium">
                      Posted at: {new Date(it.first_post_at).toLocaleString()}
                    </div>
                  ) : it.step_sent_at && cohortId !== "posted" ? (
                    <div className="text-[11px] text-muted-foreground">
                      {cohortId === "in_flight" ? "D3 sent" : "Email sent"}: {new Date(it.step_sent_at).toLocaleString()}
                    </div>
                  ) : null}
                  {/* Only show the cron-observation timestamp when it's
                      MEANINGFULLY different from the displayed action time
                      (>1 minute apart). Otherwise it just duplicates and confuses. */}
                  {it.completed_at && it.completed_reason && (() => {
                    const action = it.completed_reason === "posted" ? it.first_post_at : null;
                    if (!action) return (
                      <div className="text-[11px] text-muted-foreground">
                        Completed ({it.completed_reason}): {new Date(it.completed_at).toLocaleString()}
                      </div>
                    );
                    const drift = Math.abs(new Date(it.completed_at).getTime() - new Date(action).getTime());
                    if (drift < 60_000) return null;
                    return (
                      <div className="text-[10px] text-muted-foreground/70">
                        Detected by cron: {new Date(it.completed_at).toLocaleString()}
                      </div>
                    );
                  })()}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
